import { useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ROBOT_STATUS_LABELS, RobotStatus } from '@bhops/shared';
import { optionGroupsApi, type OptionValueDto } from '@/api/option-groups';
import { extractApiError } from '@/api/client';
import { Button, Input } from '@/components/Form';

// Plan v0.4.0 §N-2 (v0.3.0 §G-3 follow-up) — admin tooling for
// the OptionGroup picklist registry. EditableSelect already lets
// managers add values inline at the call site; this page
// centralizes the same operations + adds archive/restore so
// admins can audit + clean up groups.
//
// v1 scope:
//   - Hardcoded list of known group keys (the §G-3 / §H-3 sites)
//   - Per-group: list active + archived values
//   - Add new value (mutation reuses optionGroupsApi.addValue)
//   - Archive (soft, 기존 endpoint) / Restore (POST .../restore) /
//     완전 삭제 (DELETE .../permanent — 서버가 보관된 값만 허용하는
//     archive-first 가드. 사용 중 값은 보관만 되고 완전 삭제 불가.)
//     windows-ux-edit (2026-06, 슬라이드3, Phase1) — 복원/완전삭제 버튼 UI.
//   - Drag-to-reorder (sortOrder) deferred to v0.4.0 §N-2 v2
//
// Permission: ADMIN-only route (gated by ProtectedRoute in
// router.tsx). Manager+ users still get inline add via
// EditableSelect at the modal call sites.

// v1.0.14 §4 — 그룹 메타에 `locked` 플래그 추가. 코드 잠금 enum 은
// 사용자 피드백 ("선택 가능 항목 추가/삭제 필요") 에 따라 화면에는
// 노출하되 편집 불가 + 코드 변경 요청 안내 문구를 보여준다.
interface GroupMeta {
  key: string;
  label: string;
  description: string;
  /** true = 코드 잠금 (TypeScript enum 기반). 추가/삭제는 코드 배포 필요. */
  locked?: boolean;
  /** 코드 잠금 그룹의 현재 enum 값 목록 (display only). */
  enumValues?: ReadonlyArray<{ value: string; label: string }>;
}

const KNOWN_GROUPS: ReadonlyArray<GroupMeta> = [
  // ===== 사용자 편집 가능 (OptionGroup) =====
  {
    key: 'robot.color',
    label: '로봇 색상',
    description: '로봇의 색상 칸에 쓰는 값입니다. 예: 화이트, 블랙, 회색',
  },
  {
    key: 'robot.loaderType',
    label: '로더 종류',
    description: '로봇의 로더 종류 칸에 쓰는 값입니다. 예: 485, ttl',
  },
  // FB2 §4A/5A — robot.model 잠금 해제. Model 테이블이 아닌 OptionGroup
  // `robot.model` 기반(마이그레이션 20260608000002 이 기존 7개 코드를 seed)
  // 으로 운영자가 추가/보관(soft-archive) 가능. 기존 robot 의 modelCode 는
  // archived 값이라도 라벨이 resolve 되어 표시에 영향 없음.
  {
    key: 'robot.model',
    label: '로봇 모델',
    description: '로봇의 모델 칸에 쓰는 값입니다. 예: GEN1, GEN2, HEADLESS, ZEPHYR',
  },
  // FB2 §4A/5A — 모델별 '구분 사양'(variant) 축 값. Model.variantOptionGroupKey
  // 가 참조. 로봇 폼·제조계획·고객마스터 모달의 EditableSelect 가 모델에 따라
  // 이 그룹들을 구동한다. 운영자가 여기서 직접 관리.
  {
    key: 'robot.variant.gen1',
    label: 'GEN1 로더 통신 케이블',
    description: 'GEN1의 구분 사양입니다. 예: 485, TTL',
  },
  {
    key: 'robot.variant.gen2',
    label: 'GEN2 방향',
    description: 'GEN2와 HEADLESS의 구분 사양입니다. 예: Left, Right',
  },
  {
    key: 'robot.variant.zephyr',
    label: 'Zephyr 타입',
    description: 'Zephyr의 구분 사양입니다. 예: 기본, 갈비',
  },
  {
    key: 'customer.salesOwnerCode',
    label: '영업 담당 코드',
    description: '고객사와 계약에 적는 영업 담당 코드입니다. 예: J, A, K, T, D, BH',
  },
  // 이슈1d — 고객 유형/렌탈사를 관리 그룹에 등록(누락돼 있어 보관값 복원/완전삭제·
  // 사용건수 도구가 이 그룹들엔 닿지 못했다). 신규 등록 모달의 EditableSelect 및
  // 고객사 마스터 필터가 이 그룹들을 구동한다.
  {
    key: 'customer.segment',
    label: '고객 유형',
    description: '고객사 마스터의 고객 유형입니다. 예: 프랜차이즈 본사, 독립 레스토랑, 카페',
  },
  {
    key: 'customer.rentalCompany',
    label: '렌탈사',
    description: '고객사 마스터의 렌탈사입니다. 예: AJ, 롯데렌탈',
  },
  {
    key: 'deal.contractType',
    label: '계약 유형',
    description: '계약을 만들 때 고르는 계약 유형입니다. 예: 렌탈, 매입',
  },
  {
    key: 'deal.contractDuration',
    label: '계약 기간',
    description: '계약을 만들 때 고르는 계약 기간입니다. 예: 12개월, 24개월',
  },
  // v1.0.15+ 고객사 마스터 통합 시트 (Notion DB 매핑)
  {
    key: 'customer.grillSelectorType',
    label: '가스 타입',
    description: '고객사 마스터의 가스 타입입니다. 예: LPG, LNG, 자체, 전기그리들, 도시가스',
  },
  {
    key: 'customer.grillSelectorInstall',
    label: '가스 설치 상태',
    description:
      '고객사 마스터의 가스 설치 상태입니다. 예: 자체 제작, 발주 전, 발주 완료, 설치완료',
  },
  {
    key: 'contract.robotDirection',
    label: '로봇 방향 (정/역)',
    description: '계약 라인의 로봇 방향입니다. 예: 정1, 역1, 정1 역1, 정3 역3',
  },
  {
    key: 'contract.robotSetting',
    label: '로봇 세팅 (적용 메뉴)',
    description: '계약 라인에 적는 적용 메뉴입니다. 예: 삼겹/목살 직화 마이야르, 스테이크',
  },
  {
    key: 'contract.grillPlate',
    label: '불판',
    description: '계약 라인의 불판 종류입니다. 예: 질화처리, 맥시 마이야르, 주물',
  },
  {
    key: 'contract.specialParts',
    label: '특수부위',
    description:
      '계약 라인의 특수부위입니다. 여러 개를 고를 수 있습니다. 예: 항정, 가브리, 갈매기, 꼬들살',
  },
  // ===== 코드 잠금 (enum) =====
  // FB2 §4A/5A — robot.model 은 위 편집 가능 섹션으로 이동(OptionGroup 기반).
  // 아래는 enum/state-machine/회계와 강결합되어 코드 잠금 유지.
  {
    key: '__locked.robot.ownerEntity',
    label: '소유 법인',
    description:
      '법인은 비욘드허니컴과 허니컴로보틱스 두 곳뿐입니다. 법인을 늘리려면 회계, 세무, 권한 설정을 함께 바꿔야 합니다.',
    locked: true,
    enumValues: [
      { value: 'BEYOND_HONEYCOMB', label: '비욘드허니컴' },
      { value: 'HONEYCOMB_ROBOTICS', label: '허니컴로보틱스' },
    ],
  },
  {
    key: '__locked.robot.status',
    label: '로봇 상태',
    description:
      '상태가 바뀌는 순서가 코드에 정해져 있습니다. 새 상태를 넣으려면 그 순서도 함께 정해야 합니다.',
    locked: true,
    // 2026-09-23 — 정본에서 만든다. 종전엔 다섯 개를 손으로 적어 두어 설치 대기·재고·
    // 폐기 예정·기타가 빠져 있었다("잠긴 목록"이라는 화면이 실제 목록과 달랐다).
    enumValues: Object.values(RobotStatus).map((value) => ({
      value,
      label: ROBOT_STATUS_LABELS[value],
    })),
  },
  {
    key: '__locked.robot.locationCategory',
    label: '위치 카테고리',
    description: '대시보드와 보고서의 집계 기준입니다. 값을 늘리면 여러 화면을 함께 고쳐야 합니다.',
    locked: true,
    enumValues: [
      { value: 'FACTORY', label: '공장' },
      { value: 'WAREHOUSE', label: '창고' },
      { value: 'CUSTOMER_SITE', label: '고객 현장' },
      { value: 'AS_CENTER', label: 'AS 센터' },
      { value: 'DISPOSAL', label: '폐기' },
    ],
  },
  {
    key: '__locked.robot.verificationStatus',
    label: '검증 상태',
    description:
      '자산 확인 기록에 쓰는 값입니다. 자산 점검 기능과 묶여 있어 여기서 바꿀 수 없습니다.',
    locked: true,
    enumValues: [
      { value: 'CONFIRMED', label: '확인됨' },
      { value: 'PHANTOM', label: '미확인 자산' },
      { value: 'TENTATIVE_OWNERSHIP', label: '소유 재검토' },
      { value: 'MISSING_SERIAL', label: 'SN 소실' },
    ],
  },
];

export function OptionGroupsAdminPage() {
  const [selectedKey, setSelectedKey] = useState<string>(KNOWN_GROUPS[0]?.key ?? '');
  const [showArchived, setShowArchived] = useState(false);

  const editableGroups = KNOWN_GROUPS.filter((g) => !g.locked);
  const lockedGroups = KNOWN_GROUPS.filter((g) => g.locked);
  const selected = KNOWN_GROUPS.find((g) => g.key === selectedKey);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header>
        <h1 className="kuun-h2" style={{ margin: 0 }}>
          옵션 그룹 관리
        </h1>
      </header>

      <div style={pageGrid}>
        {/* Left rail: group selector — editable + locked sections */}
        <section style={leftCol}>
          <div style={groupSectionLabel}>편집 가능 ({editableGroups.length})</div>
          {editableGroups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setSelectedKey(g.key)}
              style={groupRow(g.key === selectedKey)}
            >
              <div style={{ fontSize: 13, fontWeight: 500 }}>{g.label}</div>
              <div style={groupKeyHint}>{g.key}</div>
            </button>
          ))}

          <div style={groupSectionLabel}>코드 잠금 ({lockedGroups.length})</div>
          {lockedGroups.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setSelectedKey(g.key)}
              style={groupRow(g.key === selectedKey)}
            >
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--fg-muted)' }}>
                {g.label} <span style={{ fontSize: 10, color: 'var(--fg-muted)' }}>(잠금)</span>
              </div>
              <div style={groupKeyHint}>값 {g.enumValues?.length ?? 0}개, 읽기 전용</div>
            </button>
          ))}
        </section>

        {/* Right pane: selected group */}
        <section style={rightCol}>
          {!selected ? (
            <div style={emptyDetail}>왼쪽에서 그룹을 선택하세요</div>
          ) : selected.locked ? (
            <LockedGroupView meta={selected} />
          ) : (
            <GroupEditor
              groupKey={selected.key}
              meta={selected}
              showArchived={showArchived}
              onToggleArchived={() => setShowArchived((v) => !v)}
            />
          )}
        </section>
      </div>
    </div>
  );
}

// v1.0.14 §4 — 코드 잠금 그룹은 enum 값을 read-only 로 표시하고
// 변경 방법을 안내한다. 실제 enum 추가/제거는 백엔드 schema + CASL
// ability + state machine 동시 변경이 필요하므로 UI 토글로 풀 수
// 없다 (사용자가 모르고 클릭해도 잘못된 기대 안 하게).
function LockedGroupView({ meta }: { meta: GroupMeta }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <header style={detailHeader}>
        <div>
          <h2 className="kuun-h3" style={{ margin: 0 }}>
            {meta.label}{' '}
            <span style={{ fontSize: 12, color: 'var(--fg-muted)', fontWeight: 400 }}>(잠금)</span>
          </h2>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
            {meta.description}
          </div>
        </div>
      </header>

      <div style={lockedNotice}>
        <strong style={{ fontSize: 13 }}>이 항목은 코드로 관리됩니다.</strong>
        <p style={{ margin: '6px 0 0', fontSize: 12, lineHeight: 1.6 }}>
          값을 더하거나 빼려면 개발팀에 요청해 주세요. 바꿀 때는 아래 항목을 함께 검토합니다.
        </p>
        <ul style={{ margin: '6px 0 0 16px', fontSize: 12, lineHeight: 1.6 }}>
          <li>데이터베이스 구조</li>
          <li>권한 설정</li>
          <li>상태가 바뀌는 규칙이 있다면 그 규칙</li>
          <li>대시보드와 보고서의 집계 기준</li>
        </ul>
      </div>

      <div style={card}>
        <div style={listHeader}>
          <strong style={{ fontSize: 13 }}>현재 값 ({meta.enumValues?.length ?? 0}개)</strong>
        </div>
        <table style={tableBase}>
          <thead>
            <tr>
              <th style={th}>코드 값</th>
              <th style={th}>한글 이름</th>
            </tr>
          </thead>
          <tbody>
            {(meta.enumValues ?? []).map((v) => (
              <tr key={v.value} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{v.value}</td>
                <td style={td}>{v.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface GroupEditorProps {
  groupKey: string;
  meta?: { label: string; description: string };
  showArchived: boolean;
  onToggleArchived: () => void;
}

function GroupEditor({ groupKey, meta, showArchived, onToggleArchived }: GroupEditorProps) {
  const queryClient = useQueryClient();
  const [draftValue, setDraftValue] = useState('');
  const [draftLabel, setDraftLabel] = useState('');

  const valuesQ = useQuery({
    queryKey: ['option-groups', groupKey, { includeArchived: showArchived }],
    queryFn: () => optionGroupsApi.listValues(groupKey, showArchived),
    staleTime: 30_000,
  });

  // data-lifecycle PR-6 — 값별 실제 사용 건수(미사용 항목 안전 삭제 안내).
  const usageQ = useQuery({
    queryKey: ['option-groups', groupKey, 'usage'],
    queryFn: () => optionGroupsApi.usage(groupKey),
    staleTime: 30_000,
  });
  const usageById = new Map((usageQ.data?.values ?? []).map((u) => [u.id, u.usageCount]));
  // hasConsumer 미확정(로딩 중) 동안엔 경고를 띄우지 않는다(false 경보 방지).
  const unreferencedGroup = usageQ.data ? !usageQ.data.hasConsumer : false;

  const addM = useMutation({
    mutationFn: () =>
      optionGroupsApi.addValue(groupKey, {
        value: draftValue.trim(),
        label: draftLabel.trim() || undefined,
      }),
    onSuccess: () => {
      // Invalidate both the admin view and the inline-edit cache
      // (EditableSelect uses ['option-groups', groupKey] without
      // the includeArchived sub-key, so a partial-key invalidate
      // catches both).
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
      setDraftValue('');
      setDraftLabel('');
    },
  });

  const archiveM = useMutation({
    mutationFn: (valueId: string) => optionGroupsApi.archiveValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
    },
  });

  // windows-ux-edit (2026-06, 슬라이드3, Phase1) — 보관 값 복원.
  const restoreM = useMutation({
    mutationFn: (valueId: string) => optionGroupsApi.restoreValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
    },
  });

  // windows-ux-edit (2026-06, 슬라이드3, Phase1) — 완전 삭제(서버 archive-first
  // 가드: 보관된 값만 허용). 비가역이므로 confirm 후 실행.
  const hardDeleteM = useMutation({
    mutationFn: (valueId: string) => optionGroupsApi.hardDeleteValue(valueId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['option-groups', groupKey] });
    },
  });

  const items = valuesQ.data ?? [];
  const active = items.filter((v) => !v.archived);
  const archived = items.filter((v) => v.archived);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <header style={detailHeader}>
        <div>
          <h2 className="kuun-h3" style={{ margin: 0 }}>
            {meta?.label ?? groupKey}
          </h2>
          <div className="kuun-caption" style={{ color: 'var(--fg-muted)', marginTop: 2 }}>
            {meta?.description ?? '설명이 없습니다.'} 그룹 키:
            <span style={{ fontFamily: 'var(--font-mono)', marginLeft: 4 }}>{groupKey}</span>
          </div>
        </div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--fg-muted)',
            whiteSpace: 'nowrap',
          }}
        >
          <input type="checkbox" checked={showArchived} onChange={onToggleArchived} />
          보관된 값도 보기
        </label>
      </header>

      {/* data-lifecycle PR-6 — 코드 미참조 그룹(정리 후보) 안내. */}
      {unreferencedGroup && (
        <div style={unusedNotice}>
          지금 어느 화면에서도 쓰지 않는 그룹입니다. 값을 모두 보관한 뒤 완전히 삭제해도 됩니다.
        </div>
      )}

      {/* Add new value */}
      <div style={addCard}>
        <strong style={{ fontSize: 12, color: 'var(--fg-muted)' }}>+ 새 옵션 추가</strong>
        <div style={addRow}>
          <Input
            placeholder="값, 예: Black"
            value={draftValue}
            onChange={(e) => setDraftValue(e.target.value)}
            style={{ flex: '1 1 140px' }}
          />
          <Input
            placeholder="한글 이름, 비워 둬도 됩니다"
            value={draftLabel}
            onChange={(e) => setDraftLabel(e.target.value)}
            style={{ flex: '1 1 140px' }}
          />
          <Button
            type="button"
            onClick={() => addM.mutate()}
            disabled={!draftValue.trim() || addM.isPending}
          >
            {addM.isPending ? '저장 중…' : '추가'}
          </Button>
        </div>
        {addM.isError && <div style={errorText}>{extractApiError(addM.error)}</div>}
      </div>

      {/* Active values */}
      <ValueList
        title={`활성 (${active.length})`}
        items={active}
        usageById={usageById}
        onArchive={(id) => archiveM.mutate(id)}
        busy={archiveM.isPending}
        empty="활성 값이 없습니다. 위에서 추가해 주세요."
      />

      {/* Archived values — 복원 / 완전 삭제 가능. */}
      {showArchived && (
        <ValueList
          title={`보관됨 (${archived.length})`}
          items={archived}
          tone="muted"
          usageById={usageById}
          onRestore={(id) => restoreM.mutate(id)}
          onHardDelete={(id) => {
            // data-lifecycle PR-6 — 사용 건수를 confirm 에 표기(0건이면 안전).
            const uses = usageById.get(id) ?? 0;
            const warn =
              uses > 0
                ? `\n주의: 이 값을 쓰는 데이터가 ${uses}건 있습니다. 완전히 삭제해도 그 데이터에 적힌 값은 그대로 남습니다.`
                : '\n이 값을 쓰는 데이터가 없어 안전하게 삭제할 수 있습니다.';
            if (
              window.confirm(`이 값을 완전 삭제합니다. 되돌릴 수 없습니다.${warn}\n계속할까요?`)
            ) {
              hardDeleteM.mutate(id);
            }
          }}
          busy={restoreM.isPending || hardDeleteM.isPending}
          empty="보관된 값이 없습니다."
        />
      )}

      {valuesQ.error && <div style={errorText}>{extractApiError(valuesQ.error)}</div>}
      {restoreM.isError && <div style={errorText}>{extractApiError(restoreM.error)}</div>}
      {hardDeleteM.isError && <div style={errorText}>{extractApiError(hardDeleteM.error)}</div>}
    </div>
  );
}

interface ValueListProps {
  title: string;
  items: OptionValueDto[];
  /** data-lifecycle PR-6 — valueId → 실제 사용 건수(미사용 안전 삭제 안내). */
  usageById?: Map<string, number>;
  /** 활성 값 → 보관(soft-archive). */
  onArchive?: (valueId: string) => void;
  /** 보관 값 → 복원. */
  onRestore?: (valueId: string) => void;
  /** 보관 값 → 완전 삭제(비가역, 서버 archive-first 가드). */
  onHardDelete?: (valueId: string) => void;
  /** 행 단위 mutation(보관/복원/삭제)이 진행 중이면 버튼 비활성. */
  busy?: boolean;
  tone?: 'default' | 'muted';
  empty: string;
}

function ValueList({
  title,
  items,
  usageById,
  onArchive,
  onRestore,
  onHardDelete,
  busy,
  tone,
  empty,
}: ValueListProps) {
  const hasActions = Boolean(onArchive || onRestore || onHardDelete);
  const showUsage = !!usageById;
  return (
    <div style={card}>
      <div style={listHeader}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
      </div>
      <table style={tableBase}>
        <thead>
          <tr>
            <th style={th}>값</th>
            <th style={th}>레이블</th>
            {showUsage && <th style={{ ...th, textAlign: 'center' }}>사용</th>}
            <th style={{ ...th, textAlign: 'center' }}>순서</th>
            {hasActions && <th style={{ ...th, textAlign: 'right' }}>액션</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((v) => (
            <tr
              key={v.id}
              style={{
                borderTop: '1px solid var(--border-subtle)',
                opacity: tone === 'muted' ? 0.55 : 1,
              }}
            >
              <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{v.value}</td>
              <td style={td}>{v.label ?? <span style={{ color: 'var(--fg-muted)' }}>—</span>}</td>
              {showUsage && (
                <td style={{ ...td, textAlign: 'center' }}>
                  {(() => {
                    const n = usageById?.get(v.id) ?? 0;
                    return (
                      <span style={n === 0 ? usageZeroBadge : usageBadge}>
                        {n === 0 ? '사용 0건' : `사용 ${n}건`}
                      </span>
                    );
                  })()}
                </td>
              )}
              <td style={{ ...td, textAlign: 'center', fontFamily: 'var(--font-mono)' }}>
                {v.sortOrder}
              </td>
              {hasActions && (
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {onRestore && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => onRestore(v.id)}
                      disabled={busy}
                    >
                      복원
                    </Button>
                  )}
                  {onHardDelete && (
                    <Button
                      type="button"
                      variant="danger"
                      onClick={() => onHardDelete(v.id)}
                      disabled={busy}
                      style={{ marginLeft: 6 }}
                    >
                      완전 삭제
                    </Button>
                  )}
                  {onArchive && (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => onArchive(v.id)}
                      disabled={busy}
                    >
                      보관
                    </Button>
                  )}
                </td>
              )}
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={3 + (showUsage ? 1 : 0) + (hasActions ? 1 : 0)} style={emptyRow}>
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================

const pageGrid: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(220px, 280px) 1fr',
  gap: 16,
  alignItems: 'start',
};

const leftCol: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  display: 'flex',
  flexDirection: 'column',
};

const rightCol: CSSProperties = {
  minHeight: 480,
};

function groupRow(active: boolean): CSSProperties {
  return {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '10px 12px',
    background: active ? 'var(--bg-selected)' : 'transparent',
    border: 'none',
    borderBottom: '1px solid var(--border-subtle)',
    cursor: 'pointer',
    fontFamily: 'inherit',
  };
}

const detailHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
};

const card: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  padding: 12,
};

const addCard: CSSProperties = {
  ...card,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  background: 'var(--bg-sunken)',
};

const addRow: CSSProperties = {
  display: 'flex',
  gap: 8,
  alignItems: 'center',
  flexWrap: 'wrap',
};

const listHeader: CSSProperties = {
  marginBottom: 8,
};

const tableBase: CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
};

const th: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  padding: '6px 10px',
  borderBottom: '1px solid var(--border-subtle)',
};

const td: CSSProperties = {
  fontSize: 13,
  padding: '6px 10px',
  verticalAlign: 'middle',
};

const emptyRow: CSSProperties = {
  padding: 16,
  textAlign: 'center',
  fontSize: 12,
  color: 'var(--fg-muted)',
};

const emptyDetail: CSSProperties = {
  padding: 32,
  textAlign: 'center',
  fontSize: 13,
  color: 'var(--fg-muted)',
};

const errorText: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-danger)',
};

// data-lifecycle PR-6 — 미참조 그룹 안내 + 사용 건수 배지.
const unusedNotice: CSSProperties = {
  border: '1px solid var(--bg-warning-subtle)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  borderRadius: 'var(--r-card)',
  padding: '8px 12px',
  fontSize: 12,
};
const usageBadge: CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 'var(--r-pill)',
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--bg-info-subtle)',
  color: 'var(--fg-on-info-tint)',
};
const usageZeroBadge: CSSProperties = {
  display: 'inline-block',
  padding: '1px 8px',
  borderRadius: 'var(--r-pill)',
  fontSize: 11,
  fontWeight: 600,
  background: 'var(--bg-success-subtle)',
  color: 'var(--fg-on-success-tint)',
};

// v1.0.14 §4 — locked group view styles
const groupSectionLabel: CSSProperties = {
  padding: '8px 12px',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--fg-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const groupKeyHint: CSSProperties = {
  fontSize: 11,
  color: 'var(--fg-muted)',
  fontFamily: 'var(--font-mono)',
  marginTop: 2,
};

const lockedNotice: CSSProperties = {
  border: '1px solid var(--bg-warning-subtle)',
  background: 'var(--bg-warning-subtle)',
  color: 'var(--fg-on-warning-tint)',
  borderRadius: 'var(--r-card)',
  padding: 12,
};
